'use strict';

import LengthValidation from 'helpers/length-validation'
import PatternValidation from 'helpers/pattern-validation'
import RequiredValidation from 'helpers/required-validation'
import Logger from '../never-knowingly-undersold-claim/Logger'
import Analytics from 'helpers/analytics/analytics';

export const ValidatableInput = {
  validatable: function(element, options) {
    const BACKSPACE = 8;
    const DELETE = 46;
    const ENTER = 13;

    if (element === undefined) {
      throw new Error('The element provided was empty');
    }

    function addAnalyticsError(errorText) {
      if (!errorText && options.errorNode) {
        errorText = options.errorNode.innerHTML;
      }

      if (errorText.length > 0) {
        Analytics.addError(errorText, true);
      }
    }

    const validatableField = {
      focus: function() {
        element.focus();
      },
      removeClass: function(currentClass) {
        element.classList.remove(currentClass);
      },

      replace: function(currentClass, newClass) {
        validatableField.removeClass(currentClass);
        element.classList.add(newClass);
      },

      resetStyles: function() {
        let target = element.classList.contains('pretty-select') ? element.parentNode : element;

        if(target.classList.contains('pretty-select-wrapper')) {
          target.classList.remove('pretty-select-wrapper--invalid');
          target.classList.remove('pretty-select-wrapper--valid');
        } else if (target.classList.contains('email-input')) {
          target.classList.remove('email-input--invalid');
          target.classList.remove('email-input--valid');
        } else if (target.classList.contains('textarea')) {
          target.classList.remove('textarea--invalid');
          target.classList.remove('textarea--valid');
        } else {
          target.classList.remove('input--invalid');
          target.classList.remove('input--valid');
        }
      },

      makeInvalid: function() {
        let target = element.classList.contains('pretty-select') ? element.parentNode : element;

        if(target.classList.contains('pretty-select-wrapper')) {
          target.classList.add('pretty-select-wrapper--invalid');
          target.classList.remove('pretty-select-wrapper--valid');
        } else if (target.classList.contains('email-input')) {
          target.classList.add('email-input--invalid');
          target.classList.remove('email-input--valid');
        } else if (target.classList.contains('textarea')) {
          target.classList.add('textarea--invalid');
          target.classList.remove('textarea--valid');
        } else {
          target.classList.add('input--invalid');
          target.classList.remove('input--valid');
        }
      },

      makeValid: function() {
        let target = element.classList.contains('pretty-select') ? element.parentNode : element;

        if(target.classList.contains('pretty-select-wrapper')) {
          target.classList.remove('pretty-select-wrapper--invalid');
          target.classList.add('pretty-select-wrapper--valid');
        } else if (target.classList.contains('email-input')) {
          target.classList.remove('email-input--invalid');
          target.classList.add('email-input--valid');
        } else if (target.classList.contains('textarea')) {
          target.classList.remove('textarea--invalid');
          target.classList.add('textarea--valid');
        } else {
          target.classList.remove('input--invalid');
          target.classList.add('input--valid');
        }
      },

      showErrorMessage: function() {
        if (element.parentNode.classList.contains('pretty-select-wrapper')) {
          element.parentNode.parentNode.appendChild(options.errorNode);
        } else if (element.nextSibling) {
          element.parentNode.insertBefore(options.errorNode, element.nextSibling);
        } else {
          element.parentNode.appendChild(options.errorNode);
        }

        addAnalyticsError()
      },

      addAnalyticsError: addAnalyticsError(),

      hideErrorMessage: function() {
        if (element.parentNode === options.errorNode.parentNode) {
          element.parentNode.removeChild(options.errorNode);
        } else if (element.tagName.toLowerCase() === "select" && element.parentNode.parentNode === options.errorNode.parentNode) {
          element.parentNode.parentNode.removeChild(options.errorNode);
        }
      }
    };

    const incompleteMessage = element.getAttribute('data-incomplete-message');
    let requiresCorrection = false;
    if (options.errorNode) {
      options.errorNode.appendChild(document.createTextNode(incompleteMessage));
    }

    function validate(text) {
      if (isValid(text)) {
        options.onValid(validatableField);
      } else {
        options.onInvalid(validatableField);
      }
    }

    ['cut', 'copy', 'paste'].forEach(function(action) {
      element.addEventListener(action, function(event) {
        validate(event.clipboardData.getData('Text'));
      })
    });

    element.addEventListener('keyup', function(event) {
      if (event.keyCode === BACKSPACE || event.keyCode === DELETE) {
        validate(element.value);
      }

      if (element.value === '') {
        if (event.keyCode !== ENTER) {
          options.onClear(validatableField);
        }
      } else {
        if (requiresCorrection) {
          options.onCorrection(validatableField);
          requiresCorrection = false;
        }

        validate(element.value);
      }
    });

    element.addEventListener('blur', function() {
      if (element.value === '') {
        return;
      }

      if (isValid(element.value)) {
        options.onValid(validatableField)
      } else {
        options.onIncomplete(validatableField)
      }
    });

    if(element.tagName.toLowerCase() === "select") {
      element.addEventListener('change', function() {
        if(element.value === '') {
          return;
        }
        (isValid(element.value) ? options.onValid : options.onIncomplete)(validatableField);
      });
    }

    function isValid(text) {
      let valid = true;
      if (!element.disabled) {
        for (let i = 0; i < options.validators.length; i++) {
          valid = options.validators[i].test(text.trim()) && valid;
        }
      }

      return valid;
    }

    return {
      validateSubmission: function(form) {
        if (!isValid(element.value)) {
          requiresCorrection = true;
          options.onIncomplete(validatableField);
          form.stop(validatableField);
          return false;
        } else {
          return true;
        }
      },
      showErrorMessage: validatableField.showErrorMessage,
      hideErrorMessage: validatableField.hideErrorMessage,
      getValidationElement: () => element,
      getFocusElement: () => element
    };
  }
};

export default function ValidatableFormField(element, extraValidators, options) {
  const logger = options.useLogger && new Logger(`ValidatableKNUField:${element.id}`);

  function displayIncompleteErrorMessage(inputField) {
    if(logger) {
      logger.debug('displayIncompleteErrorMessage');
    }
    inputField.makeInvalid();
    inputField.showErrorMessage();
  }

  function removeErrorMessage(inputField) {
    if(logger) {
      logger.debug('removeErrorMessage');
    }
    inputField.hideErrorMessage();
  }

  function displayValidField(inputField) {
    if(logger) {
      logger.debug('displayValidField');
    }
    inputField.makeValid();
    inputField.hideErrorMessage();
  }

  function createErrorNode(errorClass) {
    if(logger) {
      logger.debug('createErrorNode');
    }
    const errorMessage = document.createElement('small');
    errorMessage.classList.add(errorClass);
    return errorMessage;
  }

  function clearValidation(inputField) {
    if(logger) {
      logger.debug('clearValidation');
    }
    inputField.hideErrorMessage();
    inputField.resetStyles();
  }

  let validators = [new LengthValidation(element), new PatternValidation(element), new RequiredValidation(element)];

  return ValidatableInput.validatable(element, {
    errorNode: createErrorNode('never-knowingly-undersold-error-msg'),
    validators: extraValidators ? validators.concat(extraValidators) : validators,
    onIncomplete: displayIncompleteErrorMessage,
    onCorrection: removeErrorMessage,
    onValid: displayValidField,
    onInvalid: () => {},
    onClear: clearValidation
  });
}

